import { createDomain, sample, combine, attach } from 'effector'
import { createGate } from 'effector-react'
import {
  debtModel,
  cashModalModel,
  depositRepaymentModalModel,
  formModel,
} from '~/entities/Debt'
import { AxiosErrorType, Debt } from '~/shared/api'
import { DebtStatusesEnum } from '~/shared/config/enums'
import { logger } from '~/shared/lib/logger'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { isString } from '~/shared/lib/utils'
import { formButtonsModel } from '~/shared/ui/Form'

export const Gate = createGate<{ debtId: UniqueId }>()

export const domain = createDomain('features.debts.view')

export const $debtId = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { debtId }) => debtId)

export const $debt = combine($debtId, debtModel.$debtsCache, (id, cache) => {
  if (!id) return null
  return cache.map[id] ?? null
})

sample({
  clock: $debtId,
  filter: isString,
  target: debtModel.requestFx,
})

// Update
export const formSubmitted = domain.createEvent<formModel.FormValues>()
export const debtUpdateFx = attach({
  effect: debtModel.updateFx,
  source: $debtId,
  mapParams: (
    { total, paymentCost, comment, carOption }: formModel.FormValues,
    debtId,
  ) => {
    return {
      debtId: debtId as UniqueId,
      values: {
        total,
        paymentCost,
        comment,
        carId: carOption as UniqueId,
      },
    }
  },
})

sample({
  clock: formSubmitted,
  target: debtUpdateFx,
})

sample({
  clock: debtUpdateFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.saved successfully' as const,
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, formButtonsModel.editingEnded],
})

sample({
  clock: debtUpdateFx.failData,
  fn(e) {
    logger.error(e)
    return {
      messageI18n: 'alert.error saving debt data' as const,
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Status change
export const sendStatusChangeFx = domain.createEffect<
  {
    status: DebtStatusesEnum
    debtId: UniqueId
  },
  void
>({
  async handler({ status, debtId }) {
    await Debt.changeStatus(status, debtId)
  },
})
export const statusChangeFx = attach({
  effect: sendStatusChangeFx,
  source: $debtId,
  mapParams: (status: DebtStatusesEnum, debtId) => ({
    status,
    debtId: debtId as UniqueId,
  }),
})

sample({
  clock: statusChangeFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.status successfully changed' as const,
      variant: 'success' as const,
    }
  },
  target: snackbarEnqueued,
})
sample({
  clock: statusChangeFx.failData,
  fn(e) {
    logger.error(e)
    return {
      messageI18n: 'alert.status changed error' as const,
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Cash deposit
export const sendCashDepositFx = domain.createEffect<
  {
    formValues: cashModalModel.FormValues
    fn: () => Promise<void>
    debtId: string
  },
  void,
  AxiosErrorType
>({
  async handler({ formValues, debtId, fn }) {
    await Debt.cashDeposit(formValues, debtId)
    await fn()
  },
})

export const cashDeposit = domain.createEvent<{
  formValues: cashModalModel.FormValues
  fn: () => Promise<void>
}>()

export const cashDepositFx = attach({
  effect: sendCashDepositFx,
  source: $debtId,
  mapParams: (
    params: {
      formValues: cashModalModel.FormValues
      fn: () => Promise<void>
    },
    debtId,
  ) => ({
    ...params,
    debtId: debtId as UniqueId,
  }),
})

sample({
  clock: cashDeposit,
  target: cashDepositFx,
})

sample({
  clock: cashDepositFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.successful debt repayment' as const,
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, cashModalModel.closeCash],
})

sample({
  clock: cashDepositFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Is erroneous change
export const sendIsErroneousChangeFx = domain.createEffect<
  { isErroneous: boolean; debtId: UniqueId },
  { isErroneous: boolean }
>({
  async handler({ isErroneous, debtId }) {
    await Debt.updateErroneous(isErroneous, debtId)
    return { isErroneous }
  },
})
export const isErroneousChangeFx = attach({
  effect: sendIsErroneousChangeFx,
  source: $debtId,
  mapParams: (isErroneous: boolean, debtId) => ({
    isErroneous,
    debtId: debtId as UniqueId,
  }),
})

sample({
  clock: isErroneousChangeFx.doneData,
  fn({ isErroneous }) {
    return {
      messageI18n: isErroneous
        ? ('alert.is erroneous add' as const)
        : ('alert.is erroneous canceled' as const),
      variant: 'success' as const,
    }
  },
  target: snackbarEnqueued,
})
sample({
  clock: isErroneousChangeFx.failData,
  fn(e) {
    logger.error(e)
    return {
      messageI18n: 'alert.cancellation error' as const,
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Deposit balance
export const depositBalanceRequested = domain.createEvent()

export const fetchDepositBalanceFx = domain.createEffect<
  UniqueId,
  number,
  AxiosErrorType
>({
  async handler(debtId) {
    return Debt.getDepositBalance(debtId)
  },
})

export const $depositBalance = domain
  .createStore<number | null>(null)
  .on(fetchDepositBalanceFx.doneData, (_, amount) => amount)
  .reset([Gate.close, fetchDepositBalanceFx.fail])

sample({
  clock: depositBalanceRequested,
  source: $debtId,
  filter: isString,
  target: fetchDepositBalanceFx,
})

sample({
  clock: fetchDepositBalanceFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Deposit repayment
export const sendDepositRepaymentFx = domain.createEffect<
  {
    formValues: depositRepaymentModalModel.FormValues
    fn: () => Promise<void>
    debtId: UniqueId
  },
  void,
  AxiosErrorType
>({
  async handler({ formValues, debtId, fn }) {
    await Debt.depositRepayment(formValues, debtId)
    await fn()
  },
})

export const depositRepayment = domain.createEvent<{
  formValues: depositRepaymentModalModel.FormValues
  fn: () => Promise<void>
}>()

export const depositRepaymentFx = attach({
  effect: sendDepositRepaymentFx,
  source: $debtId,
  mapParams: (
    params: {
      formValues: depositRepaymentModalModel.FormValues
      fn: () => Promise<void>
    },
    debtId,
  ) => ({
    ...params,
    debtId: debtId as UniqueId,
  }),
})

sample({
  clock: depositRepayment,
  target: depositRepaymentFx,
})

sample({
  clock: depositRepaymentFx.doneData,
  fn() {
    return {
      messageI18n: 'alert.successful deposit repayment' as const,
      variant: 'success' as const,
    }
  },
  target: [snackbarEnqueued, depositRepaymentModalModel.closeDepositRepayment],
})

sample({
  clock: depositRepaymentFx.failData,
  fn(e) {
    logger.error(e)
    return {
      message: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: snackbarEnqueued,
})

// Остаток депозита меняется и при успехе, и при отказе TMS из-за нехватки средств.
sample({
  clock: [depositRepaymentFx.done, depositRepaymentFx.fail],
  target: depositBalanceRequested,
})

// Debt Update
sample({
  clock: [
    statusChangeFx.doneData,
    cashDepositFx.doneData,
    isErroneousChangeFx.doneData,
    depositRepaymentFx.doneData,
  ],
  source: $debtId,
  filter: isString,
  target: debtModel.requestSilentFx,
})
